import React from "react"
import PropTypes from "prop-types"
import * as S from "Components/Molecules/ChevronTimelineCompact/style"

/**
 * ChevronTimelineCompact Component
 *
 * Compact horizontal timeline with chevron arrows for bill listing cards.
 * Displays legislative progress with text labels inside colored boxes.
 *
 * Features:
 * - Compact horizontal layout optimized for card views
 * - Text labels inside colored boxes (not above)
 * - Chevron arrows between steps
 * - Supports 5 visual states: completed, current, pending, vetoed, final-success
 * - Icons for vetoed (X) and final-success (check) states
 * - Responsive design with text truncation
 *
 * @param {Object} props
 * @param {Array} props.steps - Array of timeline step objects
 * @param {string} props.steps[].id - Unique identifier for the step
 * @param {string} props.steps[].label - Display label for the step
 * @param {string} props.steps[].status - Visual state: completed|current|pending|vetoed|final-success
 * @param {string} props.steps[].icon - Icon type: check|x|null
 */
const ChevronTimelineCompact = ({ steps }) => {
    if (!steps || steps.length === 0) {
        return null
    }

    /**
     * Render icon for vetoed or final-success states
     */
    const renderIcon = (step) => {
        if (step.icon === "x") {
            return (
                <S.Icon type="x">
                    ✕
                </S.Icon>
            )
        }

        if (step.icon === "check") {
            return (
                <S.Icon type="check">
                    ✓
                </S.Icon>
            )
        }

        return null
    }

    return (
        <S.Container data-auto-cy="MoleculeChevronTimelineCompact">
            <S.Steps>
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <S.Step status={step.status}>
                            <S.Label>
                                {renderIcon(step)}
                                {step.label}
                            </S.Label>
                        </S.Step>
                        {index < steps.length - 1 && (
                            <S.Arrow>
                                →
                            </S.Arrow>
                        )}
                    </React.Fragment>
                ))}
            </S.Steps>
        </S.Container>
    )
}

ChevronTimelineCompact.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            status: PropTypes.oneOf([
                "completed",
                "current",
                "pending",
                "vetoed",
                "final-success",
            ]).isRequired,
            icon: PropTypes.oneOf(["check", "x", null]),
        })
    ).isRequired,
}

export default ChevronTimelineCompact

